import React, { forwardRef, useEffect, VideoHTMLAttributes } from 'react';

interface StorywindVideoComponentProps
  extends Omit<VideoHTMLAttributes<HTMLVideoElement>, 'width' | 'preload'> {
  videoUrl: string;
  poster?: string;
  showControls?: boolean;
  autoPlay?: boolean;
  preload?: boolean;
  loop?: boolean;
  mute?: boolean;
  onStartedPlaying?: () => void;
  width?: string | number;
  className?: string;
  style?: React.CSSProperties;
}

const Video = forwardRef<HTMLVideoElement, StorywindVideoComponentProps>(
  (
    {
      videoUrl,
      poster,
      showControls = false,
      autoPlay = false,
      preload = true,
      loop = true,
      mute = true,
      width,
      onStartedPlaying,
      className,
      style,
      ...rest
    },
    ref
  ) => {
    useEffect(() => {
      // Fixes react bug where "muted" HTML attribute is removed by react-dom. (7/6/2018)
      // https://github.com/facebook/react/issues/10389

      // Required for autoplay on chrome
      // https://developers.google.com/web/updates/2017/09/autoplay-policy-changes (6/6/2018)
      if (ref && 'current' in ref && ref.current && mute) {
        ref.current.setAttribute('muted', '1');
      }
    }, [mute]);

    const autoPlayProp = autoPlay ? { autoPlay: true } : {};

    return (
      <video
        ref={ref}
        key={videoUrl}
        poster={poster}
        width={width}
        onPlaying={onStartedPlaying}
        controls={showControls}
        controlsList="nodownload" // don't show download button on previews
        muted={mute}
        loop={loop}
        className={className}
        style={style}
        {...autoPlayProp}
        {...rest}
        preload={preload ? 'auto' : 'none'}
        // React 17+ has proper support for disableRemotePlayback.
        /* eslint-disable-next-line @typescript-eslint/ban-ts-comment */
        // @ts-ignore
        disableremoteplayback="true" /* eslint-disable-line react/no-unknown-property */
      >
        <source src={videoUrl} type="video/mp4" />
      </video>
    );
  }
);

export default Video;
